import {FC, useEffect, useRef, useState} from "react";
import {useQuery} from "@tanstack/react-query";
import {doc, setDoc} from "firebase/firestore";
import {Bell} from "lucide-react";
import firebase from "config/firebase";
import useUser from "contexts/User/useUser";
import axios from "api/axios";

interface SystemMessage {
  id: number;
  title: string;
  body: string;
  startAt: string;
  createdAt: string;
}

/** Patch Slate's div-based bullet rows so they render as flex containers.
 *  Tailwind classes in DB HTML are purged, so we add an explicit class. */
function patchSlateListItems(container: HTMLElement) {
  container.querySelectorAll<HTMLElement>(".li-decorator").forEach((el) => {
    const parent = el.parentElement;
    if (parent) parent.classList.add("slate-list-row");
  });
}

const AnnouncementsPage: FC = () => {
  const user = useUser() as {systemMessagesLastReadAt?: string} | null;
  const {data: messages = [], isLoading} = useQuery({
    queryKey: ["system-messages"],
    queryFn: () => axios.get("/system-messages").then((r) => r.data as SystemMessage[]),
    staleTime: 60_000,
  });

  const listRef = useRef<HTMLDivElement>(null);
  const patchDone = useRef(false);

  useEffect(() => {
    if (listRef.current && messages.length > 0 && !patchDone.current) {
      patchSlateListItems(listRef.current);
      patchDone.current = true;
    }
  }, [messages]);

  // Capture the lastReadAt at mount time — messages newer than this are "new"
  const lastReadRef = useRef<string | null | undefined>(undefined);
  if (lastReadRef.current === undefined && user) {
    lastReadRef.current = user.systemMessagesLastReadAt ?? null;
  }

  const [cleared, setCleared] = useState(false);

  useEffect(() => {
    // Mark read via Firestore
    const uid = firebase.auth.currentUser?.uid;
    if (uid) {
      setDoc(doc(firebase.db, "users", uid), {systemMessagesLastReadAt: new Date().toISOString()}, {merge: true}).catch(
        () => {},
      );
    }

    const timer = setTimeout(() => setCleared(true), 3000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="h-full overflow-y-auto bg-brand-cream">
      <div className="px-8 py-7 max-w-3xl mx-auto">
        {isLoading ? (
          <div className="space-y-3">
            {Array.from({length: 3}).map((_, i) => (
              <div key={i} className="h-20 rounded-xl bg-brand-cream-dark animate-pulse" />
            ))}
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
            <div className="w-14 h-14 rounded-2xl bg-brand-cream-dark flex items-center justify-center">
              <Bell className="w-7 h-7 text-brand-muted" />
            </div>
            <p className="text-sm text-brand-muted">No announcements.</p>
          </div>
        ) : (
          <div className="space-y-3" ref={listRef}>
            {messages.map((msg) => {
              const isNew =
                !cleared &&
                lastReadRef.current !== undefined &&
                (lastReadRef.current === null || msg.startAt > lastReadRef.current);
              return (
                <div
                  key={msg.id}
                  className="rounded-xl border p-5 transition-all duration-700"
                  style={{
                    backgroundColor: isNew ? "oklch(0.97 0.03 42)" : "white",
                    borderColor: isNew ? "oklch(0.85 0.08 42)" : "#E8E4DF",
                  }}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      {isNew && (
                        <span
                          className="w-2 h-2 rounded-full flex-shrink-0 transition-opacity duration-700"
                          style={{backgroundColor: "#F57A47"}}
                        />
                      )}
                      <h3 className="text-sm font-bold text-brand-navy">{msg.title}</h3>
                    </div>
                    <span className="text-xs text-brand-muted">
                      {new Date(msg.startAt).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </span>
                  </div>
                  <div
                    className="text-sm text-brand-navy announcement-body"
                    dangerouslySetInnerHTML={{__html: msg.body}}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AnnouncementsPage;
